import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Package } from "lucide-react";
import { MAROON, inpL } from "@/components/kiyana/LeaveForm";
import { usePager, Pager } from "@/components/kiyana/Pager";

const card = "bg-white rounded-2xl border border-slate-100";
const STAGE_LABEL = { potong: "Potong", jahit: "Jahit", finishing: "Finishing" };

function MaterialForm({ initial, units, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({ name: initial?.name || "", unit: initial?.unit || "", pack_unit: initial?.pack_unit || "", pack_qty: initial?.pack_qty || "", min_stock: initial?.min_stock || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (form.name.trim().length < 2 || !form.unit) return toast.error("Nama dan satuan wajib diisi");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), unit: form.unit, pack_unit: form.pack_unit || null, pack_qty: parseInt(form.pack_qty || 0), min_stock: parseInt(form.min_stock || 0), active: true };
      if (isEdit) await api.put(`/admin/prod/materials/${initial.id}`, payload);
      else await api.post("/admin/prod/materials", payload);
      toast.success(isEdit ? "Bahan diperbarui" : "Bahan ditambahkan"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="matf-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-display text-base font-bold text-slate-900">{isEdit ? "Edit Bahan Baku" : "Tambah Bahan Baku"}</h3>
          <button data-testid="matf-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="text-[11px] font-semibold text-slate-500">Nama Bahan <span className="text-red-500">*</span></label>
            <input data-testid="matf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inpL} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-[11px] font-semibold text-slate-500">Satuan <span className="text-red-500">*</span></label>
              <select data-testid="matf-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inpL}>
                <option value="">Pilih</option>
                {units.map((u) => <option key={u.id} value={u.label}>{u.label}</option>)}
              </select></div>
            <div><label className="text-[11px] font-semibold text-slate-500">Kemasan</label>
              <select data-testid="matf-pack" value={form.pack_unit} onChange={(e) => setForm({ ...form, pack_unit: e.target.value })} className={inpL}>
                <option value="">—</option>
                {units.filter((u) => u.label !== form.unit).map((u) => <option key={u.id} value={u.label}>{u.label}</option>)}
              </select></div>
            <div><label className="text-[11px] font-semibold text-slate-500">Isi/Kemasan</label>
              <input data-testid="matf-packqty" type="number" min="0" value={form.pack_qty} onChange={(e) => setForm({ ...form, pack_qty: e.target.value })} className={inpL} /></div>
          </div>
          {form.pack_unit && parseInt(form.pack_qty) > 0 && <p className="text-[10px] text-slate-400">Konversi: 1 {form.pack_unit} = {form.pack_qty} {form.unit} (khusus bahan ini).</p>}
          <div><label className="text-[11px] font-semibold text-slate-500">Stok Minimum</label>
            <input data-testid="matf-min" type="number" min="0" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className={inpL} /></div>
          <button data-testid="matf-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan Bahan"}</button>
        </div>
      </div>
    </div>
  );
}

function RateForm({ initial, products, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({ product_id: initial?.product_id || "", variant: initial?.variant || "", stage: initial?.stage || "potong", rate: initial?.rate || "" });
  const [busy, setBusy] = useState(false);
  const prod = products.find((p) => p.id === form.product_id);
  const submit = async () => {
    if (!(parseInt(form.rate) > 0)) return toast.error("Tarif wajib lebih dari 0");
    setBusy(true);
    try {
      if (isEdit) await api.put(`/admin/prod/rates/${initial.id}`, { product_id: initial.product_id, variant: initial.variant, stage: initial.stage, rate: parseInt(form.rate) });
      else await api.post("/admin/prod/rates", { product_id: form.product_id || null, variant: form.variant || null, stage: form.stage, rate: parseInt(form.rate) });
      toast.success("Tarif tersimpan"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ratef-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-display text-base font-bold text-slate-900">{isEdit ? "Edit Tarif" : "Tambah Tarif Upah"}</h3>
          <button data-testid="ratef-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!isEdit && (
            <>
              <div><label className="text-[11px] font-semibold text-slate-500">Produk (kosong = tarif default semua produk)</label>
                <select data-testid="ratef-product" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value, variant: "" })} className={inpL}>
                  <option value="">Default (semua produk)</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
              {form.product_id && (
                <div><label className="text-[11px] font-semibold text-slate-500">Variasi (kosong = semua variasi)</label>
                  <select data-testid="ratef-variant" value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} className={inpL}>
                    <option value="">Semua variasi</option>
                    {(prod?.variants || []).map((v) => <option key={v.sku || v.label} value={v.label}>{v.label}</option>)}
                  </select></div>
              )}
              <div><label className="text-[11px] font-semibold text-slate-500">Tahap <span className="text-red-500">*</span></label>
                <div className="flex gap-1.5 mt-1">
                  {Object.entries(STAGE_LABEL).map(([k, lb]) => (
                    <button key={k} data-testid={`ratef-stage-${k}`} onClick={() => setForm({ ...form, stage: k })}
                      className={`flex-1 h-9 rounded-xl text-xs font-bold border transition-colors ${form.stage === k ? "bg-[#FBF3F4] text-[#7F1D2D] border-[#7F1D2D]/40" : "border-slate-200 text-slate-400"}`}>{lb}</button>
                  ))}
                </div></div>
            </>
          )}
          <div><label className="text-[11px] font-semibold text-slate-500">Tarif per pcs (Rp) <span className="text-red-500">*</span></label>
            <input data-testid="ratef-rate" type="number" min="1" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className={inpL} />
            <p className="text-[10px] text-slate-400 mt-1">Salinan tarif disimpan di setiap transaksi — mengubah tarif tidak mengubah riwayat upah.</p></div>
          <button data-testid="ratef-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan Tarif"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProductionMaster() {
  const [tab, setTab] = useState("materials");
  const [options, setOptions] = useState({ units: [], materials: [], products: [] });
  const [rates, setRates] = useState([]);
  const [matForm, setMatForm] = useState(null);
  const [rateForm, setRateForm] = useState(null);
  const [newUnit, setNewUnit] = useState("");
  const [stockFor, setStockFor] = useState(null);
  const [stockQty, setStockQty] = useState("");
  const { paged: pagedMats, pager: matPager } = usePager(options.materials);
  const { paged: pagedRates, pager: ratePager } = usePager(rates);

  const load = useCallback(() => {
    api.get("/prod/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/admin/prod/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addUnit = async () => {
    if (!newUnit.trim()) return;
    try { await api.post("/admin/prod/units", { label: newUnit.trim() }); setNewUnit(""); toast.success("Satuan ditambahkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah satuan"); }
  };
  const delUnit = async (u) => {
    try { await api.delete(`/admin/prod/units/${u.id}`); toast.success("Satuan dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };
  const delRate = async (r) => {
    try { await api.delete(`/admin/prod/rates/${r.id}`); toast.success("Tarif dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };
  const adjustStock = async () => {
    const q = parseInt(stockQty);
    if (!q) return toast.error("Qty penyesuaian wajib diisi");
    try { await api.post(`/admin/prod/materials/${stockFor.id}/adjust`, { qty: q, note: "Penyesuaian master" }); toast.success("Stok disesuaikan"); setStockFor(null); setStockQty(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyesuaikan stok"); }
  };

  const TABS = [{ id: "materials", label: "Bahan Baku" }, { id: "units", label: "Satuan" }, { id: "rates", label: "Tarif Upah Produksi" }];
  return (
    <div data-testid="prod-master-page" className="space-y-5 pb-10">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Master Produksi</h1>
        <p className="text-xs text-slate-400 mt-0.5">Bahan baku, satuan (dengan konversi kemasan per bahan), dan tarif upah per produk/tahap.</p>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`pmaster-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#7F1D2D] text-[#7F1D2D] font-semibold" : "border-transparent text-slate-500"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "materials" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button data-testid="mat-add-btn" onClick={() => setMatForm({})} className="h-10 px-4 rounded-xl text-white text-xs font-semibold flex items-center gap-1.5" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Tambah Bahan</button>
          </div>
          <div className={`${card} overflow-hidden`}>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100 bg-slate-50/60"><th className="px-5 py-3">Bahan</th><th className="px-3 py-3">Satuan</th><th className="px-3 py-3">Konversi Kemasan</th><th className="px-3 py-3">Stok</th><th className="px-5 py-3 text-right">Aksi</th></tr></thead>
              <tbody>
                {pagedMats.map((m) => (
                  <tr key={m.id} data-testid={`mat-row-${m.id}`} className="border-b border-slate-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-slate-300" /><span className="font-semibold text-slate-800">{m.name}</span></div>
                      <div className="text-[10px] text-slate-400">{m.code}</div>
                    </td>
                    <td className="px-3 py-3">{m.unit}</td>
                    <td className="px-3 py-3 text-slate-500">{m.pack_unit ? `1 ${m.pack_unit} = ${m.pack_qty} ${m.unit}` : "—"}</td>
                    <td className="px-3 py-3">
                      <span className={`font-bold ${m.stock <= m.min_stock ? "text-red-500" : "text-slate-800"}`}>{m.stock?.toLocaleString("id-ID")} {m.unit}</span>
                      {m.stock <= m.min_stock && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-500 font-semibold">Menipis</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`mat-stock-${m.id}`} onClick={() => setStockFor(m)} className="h-8 px-3 rounded-lg border border-slate-200 text-[10px] font-semibold text-slate-500">Stok +/-</button>
                        <button data-testid={`mat-edit-${m.id}`} onClick={() => setMatForm(m)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!options.materials.length && <tr><td colSpan={5} className="py-10 text-center text-slate-400">Belum ada bahan baku.</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager {...matPager} testid="mat-pager" />
        </div>
      )}

      {tab === "units" && (
        <div className="space-y-3 max-w-lg">
          <div className="flex gap-2">
            <input data-testid="unit-new" value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder="Satuan baru (mis. lusin)" className={inpL} />
            <button data-testid="unit-add" onClick={addUnit} className="h-10 px-4 rounded-xl text-white text-xs font-semibold flex-shrink-0" style={{ background: MAROON }}>Tambah</button>
          </div>
          <div className={`${card} divide-y divide-slate-50`}>
            {options.units.map((u) => (
              <div key={u.id} data-testid={`unit-row-${u.label}`} className="px-5 py-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">{u.label} {u.is_system && <span className="text-[9px] text-slate-400 font-normal">• bawaan</span>}</span>
                {!u.is_system && <button data-testid={`unit-del-${u.label}`} onClick={() => delUnit(u)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "rates" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button data-testid="rate-add-btn" onClick={() => setRateForm({})} className="h-10 px-4 rounded-xl text-white text-xs font-semibold flex items-center gap-1.5" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Tambah Tarif</button>
          </div>
          <div className={`${card} overflow-hidden`}>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100 bg-slate-50/60"><th className="px-5 py-3">Produk</th><th className="px-3 py-3">Variasi</th><th className="px-3 py-3">Tahap</th><th className="px-3 py-3">Tarif/pcs</th><th className="px-5 py-3 text-right">Aksi</th></tr></thead>
              <tbody>
                {pagedRates.map((r) => (
                  <tr key={r.id} data-testid={`rate-row-${r.id}`} className="border-b border-slate-50">
                    <td className="px-5 py-3 font-semibold text-slate-800">{r.product_name || <span className="text-[#7F1D2D]">Default (semua produk)</span>}</td>
                    <td className="px-3 py-3 text-slate-500">{r.variant || "Semua"}</td>
                    <td className="px-3 py-3"><span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{STAGE_LABEL[r.stage]}</span></td>
                    <td className="px-3 py-3 font-bold text-slate-800">{formatIDR(r.rate)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`rate-edit-${r.id}`} onClick={() => setRateForm(r)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`rate-del-${r.id}`} onClick={() => delRate(r)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-300 hover:text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!rates.length && <tr><td colSpan={5} className="py-10 text-center text-slate-400">Belum ada tarif.</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager {...ratePager} testid="rate-pager" />
        </div>
      )}

      {matForm && <MaterialForm initial={matForm.id ? matForm : null} units={options.units} onClose={() => setMatForm(null)} onSaved={load} />}
      {rateForm && <RateForm initial={rateForm.id ? rateForm : null} products={options.products} onClose={() => setRateForm(null)} onSaved={load} />}
      {stockFor && (
        <div data-testid="stock-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" onClick={() => setStockFor(null)}>
          <div className="bg-white w-full max-w-xs rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-base font-bold text-slate-900">Sesuaikan Stok • {stockFor.name}</h3>
            <p className="text-xs text-slate-500">Stok saat ini: <b>{stockFor.stock} {stockFor.unit}</b>. Isi +/− (mis. 100 atau -25).</p>
            <input data-testid="stock-qty" type="number" value={stockQty} onChange={(e) => setStockQty(e.target.value)} className={inpL} />
            <button data-testid="stock-submit" onClick={adjustStock} className="w-full h-11 rounded-xl text-white text-sm font-semibold" style={{ background: MAROON }}>Simpan Penyesuaian</button>
          </div>
        </div>
      )}
    </div>
  );
}
